import { useState, useEffect } from "react";
import { 
  Calendar, 
  Plus, 
  Trash2, 
  Power,
  Clock,
  Loader2,
  Workflow
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api from "@/services/api";
import { format } from "date-fns";

const CRON_PRESETS = [
  { label: "Every hour", value: "0 * * * *" },
  { label: "Every 6 hours", value: "0 */6 * * *" },
  { label: "Every day at midnight", value: "0 0 * * *" },
  { label: "Every day at 6 AM", value: "0 6 * * *" },
  { label: "Every Monday at 9 AM", value: "0 9 * * 1" },
  { label: "First day of month", value: "0 0 1 * *" },
];

const ScheduleCard = ({ schedule, pipelines, onToggle, onDelete, toggling }) => {
  const pipeline = pipelines.find(p => p.id === schedule.pipeline_id);
  
  return (
    <Card className="shadow-soft" data-testid={`schedule-${schedule.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-xl ${schedule.is_active ? "bg-green-100" : "bg-gray-100"}`}>
              <Calendar className={`w-5 h-5 ${schedule.is_active ? "text-green-600" : "text-gray-400"}`} />
            </div>
            <div>
              <h3 className="font-semibold">{schedule.name}</h3>
              <p className="text-sm text-muted-foreground">
                {pipeline?.name || "Unknown pipeline"}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Switch
              checked={schedule.is_active}
              onCheckedChange={() => onToggle(schedule.id)}
              disabled={toggling === schedule.id}
              data-testid={`toggle-schedule-${schedule.id}`}
            />
          </div>
        </div>
        
        <div className="mt-4 space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <Clock className="w-4 h-4 text-muted-foreground" />
            <code className="bg-muted px-2 py-1 rounded text-xs">
              {schedule.cron_expression}
            </code>
          </div>
          
          {schedule.next_run && (
            <p className="text-sm text-muted-foreground">
              Next run: {format(new Date(schedule.next_run), "PPp")}
            </p>
          )}
        </div>
        
        <div className="flex gap-2 mt-4 pt-4 border-t">
          <Badge variant={schedule.is_active ? "default" : "secondary"}>
            {schedule.is_active ? "Active" : "Paused"}
          </Badge>
          
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm" 
                className="ml-auto text-destructive hover:text-destructive"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Schedule</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete "{schedule.name}"? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => onDelete(schedule.id)} className="bg-destructive text-destructive-foreground">
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>
  );
};

export default function Schedules() {
  const [schedules, setSchedules] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const [form, setForm] = useState({
    name: "",
    pipeline_id: "",
    cron_expression: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [schedulesData, pipelinesData] = await Promise.all([
        api.getSchedules(),
        api.getPipelines()
      ]);
      setSchedules(schedulesData);
      setPipelines(pipelinesData);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.createSchedule(form);
      toast.success("Schedule created");
      setDialogOpen(false);
      setForm({ name: "", pipeline_id: "", cron_expression: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to create schedule");
    }
  };

  const handleToggle = async (id) => {
    setToggling(id);
    try {
      const result = await api.toggleSchedule(id);
      toast.success(result.is_active ? "Schedule activated" : "Schedule paused");
      fetchData();
    } catch (error) {
      toast.error("Failed to toggle schedule");
    } finally {
      setToggling(null);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.deleteSchedule(id);
      toast.success("Schedule deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete schedule");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loader w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="schedules-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Scheduled Runs</h2>
          <p className="text-sm text-muted-foreground">
            Automate your pipeline executions with schedules
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-schedule-btn" disabled={pipelines.length === 0}>
              <Plus className="w-4 h-4 mr-2" />
              Create Schedule
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Create Schedule</DialogTitle>
              <DialogDescription>
                Set up automated pipeline execution
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="schedule-name">Schedule Name</Label>
                <Input
                  id="schedule-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Daily ETL Run"
                  required
                  data-testid="schedule-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Pipeline</Label>
                <Select 
                  value={form.pipeline_id} 
                  onValueChange={(value) => setForm({ ...form, pipeline_id: value })}
                >
                  <SelectTrigger data-testid="schedule-pipeline-select">
                    <SelectValue placeholder="Select a pipeline" />
                  </SelectTrigger>
                  <SelectContent>
                    {pipelines.map((pipeline) => (
                      <SelectItem key={pipeline.id} value={pipeline.id}>
                        {pipeline.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Schedule Frequency</Label>
                <Select 
                  value={form.cron_expression} 
                  onValueChange={(value) => setForm({ ...form, cron_expression: value })}
                >
                  <SelectTrigger data-testid="schedule-frequency-select">
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    {CRON_PRESETS.map((preset) => (
                      <SelectItem key={preset.value} value={preset.value}>
                        {preset.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Or enter a custom cron expression below
                </p>
                <Input
                  value={form.cron_expression}
                  onChange={(e) => setForm({ ...form, cron_expression: e.target.value })}
                  placeholder="0 * * * *"
                  className="font-mono"
                  data-testid="schedule-cron-input"
                />
              </div>
              
              <DialogFooter>
                <Button type="submit" data-testid="save-schedule-btn">
                  Create Schedule
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {pipelines.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="empty-state">
            <Workflow className="empty-state-icon" />
            <h3 className="font-semibold text-lg">No Pipelines Available</h3>
            <p className="text-muted-foreground text-sm mt-1 max-w-sm">
              Create a pipeline first before scheduling automated runs
            </p>
          </CardContent>
        </Card>
      ) : schedules.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="empty-state">
            <Calendar className="empty-state-icon" />
            <h3 className="font-semibold text-lg">No Schedules Yet</h3>
            <p className="text-muted-foreground text-sm mt-1 max-w-sm">
              Create a schedule to automate your pipeline executions
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {schedules.map((schedule) => (
            <ScheduleCard
              key={schedule.id}
              schedule={schedule}
              pipelines={pipelines}
              onToggle={handleToggle}
              onDelete={handleDelete}
              toggling={toggling}
            />
          ))}
        </div>
      )}
    </div>
  );
}
